import { memo, useContext, useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'

import { DEFAULT_CODEBOOK } from '@aytm/app/stats/page/sidebar/coding/lib/constants'

import { changeResponseTagState } from '@aytm/app/stats/page/comments/actions'
import { checkSentimentStatus, getDownloadUrl } from '@aytm/app/stats/page/comments/lib/utils'

import ImageResponse from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse/ImageResponse'
import VideoResponse from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse/VideoResponse'
import AudioResponse from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse/AudioResponse'
import TextResponse from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse/TextResponse'

import ToolBox from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse/ToolBox'
import Sentiment from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse/Sentiment'
import Summary from '@aytm/app/stats/page/comments/comments/components/OEQuestionResponse/Summary'

import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'

import { OEContext } from '@aytm/app/stats/page/comments'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import style from '@aytm/app/stats/page/comments/comments/styles/OEItem.module.scss'

const OEQuestionResponse = memo(
  ({
    node,
    nodeOptions,
    toolBoxOptions,
    path,
    responseId,
    dataId,
    tags,
    answer,
    question,
    sentiment,
    best_span_str,
    hasTitle,
    voiceBusy,
    isVirtualOrXpert,
    isVideoOrImage,
    isRejected,
    setVoiceBusy,
    onUpdateSchema,
    onUpdateParams
  }) => {
    const { searchState, dispatch, dispatchCommentStatuses } = useContext(OEContext)

    const { request, sentimentOptions, isShowAllTools, isShowSentiments, isTagsManagementDisabled, isQAMode } =
      searchState
    const sentimentStatus = checkSentimentStatus(path, sentimentOptions)

    const [isDisabledToolBox, setIsDisabledToolBox] = useState(false)

    const dndTagTargetRef = useRef(null)

    const downloadUrl = useMemo(() => getDownloadUrl(question, answer), [question, answer])

    const onAddTag = async (tag, path, responseId, action) => {
      let type
      // Clone so the caller's tag object is not mutated (see _open-questions.md §OQ-27).
      const nextTag = { ...tag }
      switch (nextTag.type) {
        case 'sentiment': {
          type = 'SENTIMENT_CHANGED'
          nextTag.currentValue = sentiment
          break
        }
        default: {
          if ('modify' != action) {
            type = 'TAG_ADDED'
          }
        }
      }
      nextTag.tag = nextTag.tag.trim()

      dispatchEvent(new CustomEvent('codingLoading', { detail: { tag: nextTag, isLoading: true } }))

      await changeResponseTagState({
        tag: nextTag,
        type,
        path,
        request,
        responseId,
        action,
        setIsLoading: value => {
          if (!value) {
            dispatchEvent(new CustomEvent('codingLoading', { detail: { isLoading: false } }))
          }
        },
        onUpdateSchema,
        dispatch,
        dispatchCommentStatuses
      })
    }

    const onRemoveTag = async (tag, path, responseId) => {
      await changeResponseTagState({
        tag,
        type: 'TAG_REMOVED',
        path,
        request,
        responseId,
        action: 'delete',
        onUpdateSchema,
        dispatch,
        dispatchCommentStatuses
      })
    }

    const onDNDEnter = () => {
      dndTagTargetRef.current.classList.add(style['comment-answer-dnd-active'])
    }

    const onDNDLeave = () => {
      dndTagTargetRef.current.classList.remove(style['comment-answer-dnd-active'])
    }

    const onDNDDrop = async dropData => {
      const { tag, type, value, label, codebook } = dropData.data

      if (PageGlobals.WINDOW_UID != dropData.data.windowUID) {
        if ('sentiment' == type) return
        if (tags.some(item => item.tag.toLowerCase() == `${DEFAULT_CODEBOOK}: ${label}`.toLowerCase())) return

        dndTagTargetRef.current.classList.remove(style['comment-answer-dnd-active'])

        setIsDisabledToolBox(true)
        await onAddTag(
          {
            tag: label,
            type: type || 'manual',
            label: label,
            codebook: null
          },
          path,
          responseId,
          'create'
        )
        setIsDisabledToolBox(false)
      } else {
        if ('sentiment' == type && !sentimentStatus) return
        if ('sentiment' != type && tags.some(item => item.tag == tag)) return

        dndTagTargetRef.current.classList.remove(style['comment-answer-dnd-active'])

        setIsDisabledToolBox(true)
        await onAddTag(
          {
            tag: 'sentiment' == type ? value : codebook == undefined || null === codebook ? label : tag,
            type: type || 'manual',
            label: label,
            codebook: codebook == undefined || null === codebook ? null : codebook
          },
          path,
          responseId,
          'create'
        )
        setIsDisabledToolBox(false)
      }
    }

    const dndTagTarget =
      !isQAMode && !isTagsManagementDisabled && !isRejected && !isVirtualOrXpert
        ? ADND.targets
            .data({
              acceptTypes: ['sidebar-tag', 'sidebar-tag-sentiment']
            })
            .onEnter(onDNDEnter)
            .onLeave(onDNDLeave)
            .onDrop(onDNDDrop)
        : null

    useEffect(() => {
      if (dndTagTarget) dndTagTarget.connect(dndTagTargetRef.current)

      return () => {
        if (dndTagTarget) dndTagTarget.disconnect()
      }
    }, [dndTagTarget])

    useEventListener('dndEndTag', () => {
      dndTagTargetRef.current.classList.remove(style['comment-answer-dnd-active'])
    })

    return (
      <div
        ref={dndTagTargetRef}
        className={classNames(style['comment-answer'], {
          [style['no-title']]: !hasTitle,
          [style['active']]: isShowAllTools && !isQAMode && !isVirtualOrXpert,
          [style[`sentiment-${sentiment}`]]: sentimentStatus && isShowSentiments && !isVideoOrImage
        })}
      >
        {sentimentStatus && isShowSentiments && !isQAMode && !isVirtualOrXpert && !isVideoOrImage ? (
          <Sentiment
            keyBase={`${responseId}-question-item-${path}-${nodeOptions.lvl}`}
            className={style['sentiment-container']}
            responseId={responseId}
            sentiment={sentiment ? sentiment : 'None'}
            path={path}
            onUpdateSchema={onUpdateSchema}
          />
        ) : null}
        <div className={style['comment-answer-content']}>
          {'mn' === question.type && <Summary node={node} question={question} />}
          {Object.hasOwn(node, 'i') ? (
            <ImageResponse node={node} />
          ) : Object.hasOwn(node, 'v') ? (
            <VideoResponse
              node={node}
              path={path}
              responseId={responseId}
              downloadUrl={downloadUrl}
              isRejected={isRejected}
              onUpdateParams={onUpdateParams}
            />
          ) : Object.hasOwn(node, 't') ? (
            'object' == typeof node.t ? (
              <AudioResponse node={node} voiceBusy={voiceBusy} setVoiceBusy={setVoiceBusy} />
            ) : (
              <TextResponse node={node} best_span_str={best_span_str} />
            )
          ) : null}
        </div>
        {isShowAllTools && !isQAMode && !isRejected && !isVirtualOrXpert && (
          <div className={style['comment-tools-container']}>
            <ToolBox
              tags={tags}
              dataId={dataId}
              responseId={responseId}
              downloadUrl={downloadUrl}
              question={question}
              path={path}
              answer={answer}
              sentiment={sentiment}
              toolBoxOptions={toolBoxOptions}
              isDisabledToolBox={isDisabledToolBox}
              onAddTag={onAddTag}
              onRemoveTag={onRemoveTag}
            />
            <div
              className={`${style['comment-tools-dnd-dropzone']} ${null !== downloadUrl ? style['has-download'] : ''}`}
            >
              <div className={style['comment-tools-dnd-dropzone-inner']}>
                <div className={style['comment-tools-dnd-dropzone-inner-text']}>+ Add code</div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }
)

OEQuestionResponse.propTypes = {
  node: PropTypes.object,
  nodeOptions: PropTypes.object,
  toolBoxOptions: PropTypes.array,
  path: PropTypes.string,
  tags: PropTypes.array,
  responseId: PropTypes.number,
  dataId: PropTypes.string,
  answer: PropTypes.object,
  question: PropTypes.object,
  sentiment: PropTypes.string,
  best_span_str: PropTypes.string,
  hasTitle: PropTypes.bool,
  voiceBusy: PropTypes.bool,
  isVirtualOrXpert: PropTypes.bool,
  isVideoOrImage: PropTypes.bool,
  isRejected: PropTypes.bool,
  setVoiceBusy: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  onUpdateParams: PropTypes.func
}

OEQuestionResponse.displayName = 'QuestionResponse'

export default OEQuestionResponse

ADND().connect()
